import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { CustomerStatementKind, CustomerStatementReport, ReportExportRequest } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/States';
import { TableSkeleton } from '@/components/Table';
import {
  customerStatementKeys,
  fetchCustomerStatement,
  fetchStatementCustomers,
  type StatementPartyRef,
} from '@/features/reports/customer-statement-api';
import { downloadReportExport, type ExportFormat } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cell = (value: number) => (Math.abs(value) < 0.005 ? '' : money(value));
/** Export cells are plain decimals — the server formats them with the shop's currency. */
const plain = (value: number) => value.toFixed(2);
const plainCell = (value: number) => (Math.abs(value) < 0.005 ? '' : plain(value));

/* ── Shared by both statement pages ─────────────────────────────────────── */

/** `deviceId:sourceId` in the URL, so a statement link survives a reload. */
export function usePartyParam(name: string): [StatementPartyRef | null, (party: StatementPartyRef | null) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(name) ?? '';
  const [deviceId, source] = raw.split(':');
  const sourceId = Number(source);
  const party = deviceId && source && Number.isInteger(sourceId) ? { deviceId, sourceId } : null;
  const set = (next: StatementPartyRef | null) =>
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      if (next) merged.set(name, `${next.deviceId}:${next.sourceId}`);
      else merged.delete(name);
      return merged;
    });
  return [party, set];
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Prints only the statement: everything else on the page (and the portal's
 * chrome) is hidden, and the statement is laid out from the page's top-left.
 */
export function StatementPrintStyles() {
  return (
    <style>{`
      @media print {
        @page { margin: 12mm; }
        html, body { background: #fff !important; height: auto !important; overflow: visible !important; }
        body * { visibility: hidden !important; }
        .statement-print, .statement-print * { visibility: visible !important; }
        .statement-print { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; border: 0 !important; box-shadow: none !important; }
        .statement-print .no-print { display: none !important; }
        .statement-print table { page-break-inside: auto; }
        .statement-print tr { page-break-inside: avoid; }
        .statement-print thead { display: table-header-group; }
      }
    `}</style>
  );
}

export interface PartyOption {
  deviceId: string;
  sourceId: number;
  name: string;
  phone: string;
  branchName: string;
}

/** Search box plus a result list; the chosen party shows as a chip with Change. */
export function PartyPicker({
  label,
  placeholder,
  search,
  onSearch,
  options,
  loading,
  selected,
  selectedLabel,
  onSelect,
}: {
  label: string;
  placeholder: string;
  search: string;
  onSearch: (value: string) => void;
  options: readonly PartyOption[];
  loading: boolean;
  selected: StatementPartyRef | null;
  selectedLabel: string;
  onSelect: (party: StatementPartyRef | null) => void;
}) {
  const [open, setOpen] = useState(!selected);
  useEffect(() => {
    if (!selected) setOpen(true);
  }, [selected]);

  if (selected && !open) {
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 print:hidden">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
        <span className="text-sm font-semibold text-slate-900">{selectedLabel || '…'}</span>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 print:hidden">
      <label className="label" htmlFor={`party-search-${label}`}>
        {label}
      </label>
      <input
        id={`party-search-${label}`}
        type="search"
        className="input w-full max-w-md"
        placeholder={placeholder}
        value={search}
        autoFocus
        onChange={(event) => onSearch(event.target.value)}
      />
      <div className="mt-3 max-h-72 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-slate-500">Searching…</p>
        ) : options.length === 0 ? (
          <p className="text-sm text-slate-500">{search ? 'No matches.' : 'Nothing synced yet.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {options.map((option) => {
              const active = selected?.deviceId === option.deviceId && selected.sourceId === option.sourceId;
              return (
                <li key={`${option.deviceId}:${option.sourceId}`}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between gap-4 px-2 py-2 text-left text-sm hover:bg-slate-50 ${
                      active ? 'bg-slate-100' : ''
                    }`}
                    onClick={() => {
                      onSelect({ deviceId: option.deviceId, sourceId: option.sourceId });
                      setOpen(false);
                    }}
                  >
                    <span className="font-medium text-slate-900">{option.name || '(no name)'}</span>
                    <span className="text-slate-500">
                      {[option.phone, option.branchName].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export function StatementHeading({
  shopName,
  title,
  partyName,
  details,
  from,
  to,
}: {
  shopName: string;
  title: string;
  partyName: string;
  details: readonly string[];
  from: string;
  to: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
      <div>
        <div className="text-lg font-semibold text-slate-900">{shopName}</div>
        <div className="text-sm uppercase tracking-wide text-slate-500">{title}</div>
      </div>
      <div className="text-right text-sm">
        <div className="font-semibold text-slate-900">{partyName}</div>
        {details.filter(Boolean).map((line) => (
          <div key={line} className="text-slate-600">
            {line}
          </div>
        ))}
        <div className="mt-1 text-slate-600">
          Period: {from} to {to}
        </div>
      </div>
    </div>
  );
}

export function StatementSummary({ items }: { items: ReadonlyArray<{ label: string; value: number }> }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{money(item.value)}</div>
        </div>
      ))}
    </div>
  );
}

export interface LedgerRow {
  key: string;
  date: string;
  ref: string;
  description: ReactNode;
  debit: number;
  credit: number;
  balance: number;
  muted?: boolean;
}

/** Plain table rather than DataTable: it prints cleanly, with no scroll container clipping it. */
export function LedgerTable({
  caption,
  debitLabel,
  creditLabel,
  opening,
  rows,
  totalDebit,
  totalCredit,
  closing,
}: {
  caption: string;
  debitLabel: string;
  creditLabel: string;
  opening: number;
  rows: readonly LedgerRow[];
  totalDebit: number;
  totalCredit: number;
  closing: number;
}) {
  const th = 'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500';
  const td = 'px-3 py-1.5 align-top';
  return (
    <div className="overflow-x-auto print:overflow-visible">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="border-b border-slate-300">
          <tr>
            <th className={`${th} text-left`}>Date</th>
            <th className={`${th} text-left`}>Ref</th>
            <th className={`${th} text-left`}>Description</th>
            <th className={`${th} text-right`}>{debitLabel}</th>
            <th className={`${th} text-right`}>{creditLabel}</th>
            <th className={`${th} text-right`}>Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr className="bg-slate-50 font-medium">
            <td className={td} colSpan={5}>
              Balance brought forward
            </td>
            <td className={`${td} text-right tabular-nums`}>{money(opening)}</td>
          </tr>
          {rows.length === 0 ? (
            <tr>
              <td className={`${td} text-slate-500`} colSpan={6}>
                No entries in this period.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.key} className={row.muted ? 'text-slate-500' : 'text-slate-900'}>
                <td className={`${td} whitespace-nowrap`}>{row.date}</td>
                <td className={`${td} whitespace-nowrap`}>{row.ref}</td>
                <td className={td}>{row.description}</td>
                <td className={`${td} text-right tabular-nums`}>{cell(row.debit)}</td>
                <td className={`${td} text-right tabular-nums`}>{cell(row.credit)}</td>
                <td className={`${td} text-right tabular-nums`}>{money(row.balance)}</td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot className="border-t-2 border-slate-300 font-semibold">
          <tr>
            <td className={td} colSpan={3}>
              Closing balance
            </td>
            <td className={`${td} text-right tabular-nums`}>{money(totalDebit)}</td>
            <td className={`${td} text-right tabular-nums`}>{money(totalCredit)}</td>
            <td className={`${td} text-right tabular-nums`}>{money(closing)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** '2026-10-01' → '01 Oct 2026', read as a calendar date rather than UTC midnight. */
export function readableDate(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export interface LedgerExportLine {
  date: string;
  ref: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

/**
 * A statement's ledger as an export table: the same brought-forward row,
 * lines and closing footer {@link LedgerTable} draws.
 */
export function ledgerExportTable({
  title,
  partyName,
  branchName,
  from,
  to,
  debitLabel,
  creditLabel,
  opening,
  lines,
  totalDebit,
  totalCredit,
  closing,
  notes,
}: {
  title: string;
  partyName: string;
  branchName: string;
  from: string;
  to: string;
  debitLabel: string;
  creditLabel: string;
  opening: number;
  lines: readonly LedgerExportLine[];
  totalDebit: number;
  totalCredit: number;
  closing: number;
  notes: string[];
}): ReportExportRequest {
  return {
    title,
    subtitle: [partyName, `${readableDate(from)} – ${readableDate(to)}`, branchName ? `Branch: ${branchName}` : '']
      .filter(Boolean)
      .join(' · '),
    from,
    to,
    columns: [
      { header: 'Date', width: 2 },
      { header: 'Ref', width: 2 },
      { header: 'Description', width: 5 },
      { header: debitLabel, align: 'right', money: true, width: 2 },
      { header: creditLabel, align: 'right', money: true, width: 2 },
      { header: 'Balance', align: 'right', money: true, width: 2 },
    ],
    rows: [
      ['', '', 'Balance brought forward', '', '', plain(opening)],
      ...lines.map((line) => [
        line.date,
        line.ref,
        line.description,
        plainCell(line.debit),
        plainCell(line.credit),
        plain(line.balance),
      ]),
    ],
    totals: { label: 'Closing balance', cells: ['', '', plain(totalDebit), plain(totalCredit), plain(closing)] },
    notes,
  };
}

/* ── Customer statement ─────────────────────────────────────────────────── */

const KIND_LABELS: Record<CustomerStatementKind, string> = {
  opening: 'Opening balance',
  sale: 'Sale',
  payment: 'Payment received',
  refund: 'Refund paid',
  return: 'Sales return',
  credit_note: 'Credit note',
  waiver: 'Waived',
};

/** The account ledger as on screen; the wallet, a second table there, rides along as a summary note. */
function customerStatementTable(report: CustomerStatementReport): ReportExportRequest {
  const { wallet } = report;
  const notes = ['Positive balance = owed by the customer. Bills paid in full at the till are not listed.'];
  if (wallet.lines.length > 0 || Math.abs(wallet.openingBalance) >= 0.005) {
    notes.push(
      `Wallet (advance held for the customer): brought forward ${plain(wallet.openingBalance)}, ` +
        `in ${plain(wallet.totalIn)}, out ${plain(wallet.totalOut)}, closing ${plain(wallet.closingBalance)}.`,
    );
  }
  return ledgerExportTable({
    title: 'Customer Statement',
    partyName: report.customer.name,
    branchName: report.customer.branchName,
    from: report.from,
    to: report.to,
    debitLabel: 'Debit',
    creditLabel: 'Credit',
    opening: report.openingBalance,
    lines: report.lines.map((line) => ({
      date: line.date,
      ref: line.ref,
      description: [KIND_LABELS[line.kind], line.ref !== line.billRef ? `on ${line.billRef}` : '', line.note]
        .filter(Boolean)
        .join(' · '),
      debit: line.debit,
      credit: line.credit,
      balance: line.balance,
    })),
    totalDebit: report.totalDebit,
    totalCredit: report.totalCredit,
    closing: report.closingBalance,
    notes,
  });
}

function CustomerStatementView({ report }: { report: CustomerStatementReport }) {
  const { customer, wallet } = report;
  const showWallet = wallet.lines.length > 0 || Math.abs(wallet.openingBalance) >= 0.005;
  return (
    <div className="statement-print rounded-lg border border-slate-200 bg-white p-6">
      <StatementHeading
        shopName={report.shopName}
        title="Customer statement"
        partyName={customer.name}
        details={[customer.phone, customer.email, customer.address, customer.branchName ? `Branch: ${customer.branchName}` : '']}
        from={report.from}
        to={report.to}
      />
      <StatementSummary
        items={[
          { label: 'Opening balance', value: report.openingBalance },
          { label: 'Charged', value: report.totalDebit },
          { label: 'Paid / credited', value: report.totalCredit },
          { label: 'Closing balance', value: report.closingBalance },
        ]}
      />
      <LedgerTable
        caption="Customer account"
        debitLabel="Debit"
        creditLabel="Credit"
        opening={report.openingBalance}
        totalDebit={report.totalDebit}
        totalCredit={report.totalCredit}
        closing={report.closingBalance}
        rows={report.lines.map((line, index) => ({
          key: `${index}`,
          date: line.date,
          ref: line.ref,
          description: (
            <>
              <span className="font-medium">{KIND_LABELS[line.kind]}</span>
              {line.ref !== line.billRef ? <span className="text-slate-500"> · on {line.billRef}</span> : null}
              {line.note ? <span className="text-slate-500"> · {line.note}</span> : null}
            </>
          ),
          debit: line.debit,
          credit: line.credit,
          balance: line.balance,
        }))}
      />
      <p className="mt-2 text-xs text-slate-500">
        Positive balance = owed by the customer. Bills paid in full at the till are not listed.
      </p>

      {showWallet ? (
        <div className="mt-8">
          <h2 className="mb-2 text-sm font-semibold text-slate-900">Wallet (advance held for the customer)</h2>
          <LedgerTable
            caption="Customer wallet"
            debitLabel="In"
            creditLabel="Out"
            opening={wallet.openingBalance}
            totalDebit={wallet.totalIn}
            totalCredit={wallet.totalOut}
            closing={wallet.closingBalance}
            rows={wallet.lines.map((line, index) => ({
              key: `w${index}`,
              date: line.date,
              ref: line.ref,
              description: (
                <>
                  <span className="font-medium">{line.label}</span>
                  {line.method ? <span className="text-slate-500"> · {line.method}</span> : null}
                  {line.note ? <span className="text-slate-500"> · {line.note}</span> : null}
                </>
              ),
              debit: line.amountIn,
              credit: line.amountOut,
              balance: line.balance,
            }))}
          />
        </div>
      ) : null}
    </div>
  );
}

export function CustomerStatementPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [party, setParty] = usePartyParam('customer');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  const customersQuery = useQuery({
    queryKey: customerStatementKeys.customers(filters.branchId, debouncedSearch.trim()),
    queryFn: ({ signal }) => fetchStatementCustomers(filters.branchId, debouncedSearch.trim(), signal),
  });

  const statementQuery = useQuery({
    queryKey: customerStatementKeys.statement(party, filters.from, filters.to),
    queryFn: ({ signal }) => fetchCustomerStatement(party!, { from: filters.from, to: filters.to }, signal),
    enabled: party !== null,
  });

  return (
    <div>
      <StatementPrintStyles />
      <PageHeader
        title="Customer statement"
        description="One customer's account: balance brought forward, every charge and payment in the period with a running balance, and their wallet."
        actions={
          <>
            {statementQuery.data ? (
              <ExportMenu
                onExport={(format: ExportFormat) => downloadReportExport(customerStatementTable(statementQuery.data), format)}
              />
            ) : null}
            <Button variant="primary" disabled={!statementQuery.data} onClick={() => window.print()}>
              Print
            </Button>
          </>
        }
      />
      <div className="print:hidden">
        <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} />
      </div>
      <PartyPicker
        label="Customer"
        placeholder="Search by name or phone"
        search={search}
        onSearch={setSearch}
        options={customersQuery.data ?? []}
        loading={customersQuery.isPending}
        selected={party}
        selectedLabel={statementQuery.data?.customer.name ?? ''}
        onSelect={setParty}
      />

      {party === null ? (
        <EmptyState title="Pick a customer" description="Search above, then choose a customer to see their statement." />
      ) : (
        <AsyncSection
          state={statementQuery}
          skeleton={<TableSkeleton columns={6} />}
          errorTitle="Could not load the statement"
        >
          {(report) => <CustomerStatementView report={report} />}
        </AsyncSection>
      )}
    </div>
  );
}
